import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import projectKeys from '@/api/queries/project/keyFactory';
import stockLocationKeys from '@/api/queries/stockLocations/keyFactory';
import { graphql } from '@/gql';
import {
  TransferStockInput,
  TransferStockMutation as TTransferStockMutation,
} from '@/gql/graphql';

const TransferStockMutation = graphql(`
  mutation TransferStock($input: TransferStockInput!) {
    transferStock(input: $input) {
      fromMaterialsList {
        id
        publicId
      }
      toMaterialsList {
        id
        publicId
      }
    }
  }
`);

/**
 * Moves on-hand stock between Materials Lists via the shared transfer mutation.
 * Pass the destination Project's public id so the Project's materials views and
 * profit breakdown refresh; the source Stock Location's levels are refreshed by
 * invalidating the Stock Location queries.
 */
const useTransferStock = (args?: {
  projectId?: string;
  options?: UseMutationOptions<
    TTransferStockMutation,
    unknown,
    TransferStockInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: TransferStockInput) =>
      gqlClient.request(TransferStockMutation, { input }),
    onSuccess: (data, variables, context) => {
      if (args?.projectId) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.materialsList(args.projectId),
        });
        void queryClient.invalidateQueries({
          queryKey: projectKeys.profitBreakdown(args.projectId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: stockLocationKeys.all });
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useTransferStock;
